import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Radio from "@/components/common/Radio";
import { getDownloadFileName } from "@/components/Product/Handout/utils/getDownloadFileName";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Tag, Typography } from "@bookips/sds";
import { alpha, FormControl, FormControlLabel, RadioGroup, styled } from "@mui/material";
import { useId } from "react";
import useHandoutProductStore from "src/store/relatedProduct";
const borderColorMap = {
    default: (theme)=>theme.palette.grey[200],
    selected: (theme)=>theme.palette.primary.main,
    disabled: (theme)=>theme.palette.grey[100]
};
const backgroundColorMap = {
    default: (theme)=>theme.palette.common.white,
    selected: (theme)=>theme.palette.indigo[0],
    disabled: (theme)=>theme.palette.grey[50]
};
// SDS Typography는 sx/color prop이 없어 Tailwind className으로 색상 지정
const labelColorClassMap = {
    default: "text-primary",
    selected: "text-indigo-500",
    disabled: "text-disabled"
};
const PurchaseFormControlLabel = styled(FormControlLabel)((param)=>{
    let { theme , status  } = param;
    return {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        border: "1px solid",
        padding: theme.spacing(0.5, 1),
        borderRadius: "6px",
        borderColor: borderColorMap[status](theme),
        backgroundColor: backgroundColorMap[status](theme),
        cursor: status === "disabled" ? "not-allowed" : "pointer",
        "&:hover": {
            backgroundColor: status === "disabled" || status === "selected" ? "none" : alpha(theme.palette.grey[300], 0.2)
        }
    };
});
const getStatusAndColor = (param)=>{
    let { isOptionAvailable , optionType , selectedOption  } = param;
    let status;
    if (isOptionAvailable === "all") {
        status = optionType === selectedOption ? "selected" : "default";
    } else {
        status = isOptionAvailable === optionType ? "selected" : "disabled";
    }
    return {
        status
    };
};
const PurchaseOption = ()=>{
    const id = useId();
    const { selectedPurchaseOption , currentProduct , purchaseOption , setSelectedPurchaseOption  } = useHandoutProductStore((state)=>({
            selectedPurchaseOption: state.selectedPurchaseOption,
            currentProduct: state.currentProduct,
            purchaseOption: state.purchaseOption,
            setSelectedPurchaseOption: state.setSelectedPurchaseOption
        }));
    const { price , downloadPrice , isOriginSellable , originalFileName  } = currentProduct;
    const optionToValue = (purchaseOption === null || purchaseOption === void 0 ? void 0 : purchaseOption.length) === 2 ? "all" : purchaseOption[0];
    const handleChange = (e)=>{
        if (!optionToValue) return;
        const statusVal = optionToValue === "all" ? "all" : optionToValue;
        if (statusVal !== "all" && statusVal !== e.target.value) return;
        setSelectedPurchaseOption(e.target.value);
        //mx-208
        mixpanelEvent("Purchase Option Button Clicked", {
            "Button Name": e.target.value
        });
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(FormControl, {
            sx: {
                width: "100%"
            },
            children: /*#__PURE__*/ _jsxs(RadioGroup, {
                value: selectedPurchaseOption,
                onChange: handleChange,
                sx: {
                    flexDirection: "row",
                    gap: "8px",
                    label: {
                        margin: 0,
                        display: "flex"
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(PurchaseOptionRadio, {
                        optionType: "view",
                        price: price,
                        label: "열람+출력 (대여)",
                        selectedOption: selectedPurchaseOption,
                        isOptionAvailable: optionToValue
                    }),
                    /*#__PURE__*/ _jsx(PurchaseOptionRadio, {
                        optionType: "download",
                        // 다운로드 금액은 price의 1.5배 가격입니다. 이 downloadPrice가 없을 수 있어요. 그럴 때는 price의 1.5배(소수점 올림)로 계산해주세요. 25.03.20
                        price: downloadPrice !== null && downloadPrice !== void 0 ? downloadPrice : Math.ceil(Number(price) * 1.5),
                        // [기간 한정 할인 이벤트] 표시용 원가: 열람가의 2배. 이벤트 종료 시 originalPrice 전달 제거
                        originalPrice: Number(price) * 2,
                        label: "".concat(getDownloadFileName(isOriginSellable, originalFileName), " 다운 (소장)"),
                        selectedOption: selectedPurchaseOption,
                        isOptionAvailable: optionToValue
                    })
                ]
            })
        })
    });
};
const PurchaseOptionRadio = (param)=>{
    let { optionType , price , label , selectedOption , isOptionAvailable , originalPrice  } = param;
    const { status  } = getStatusAndColor({
        isOptionAvailable,
        optionType,
        selectedOption: selectedOption
    });
    const isOptionSelectable = isOptionAvailable === "all" || isOptionAvailable === optionType;
    // [기간 한정 할인 이벤트] 원가(열람가 2배)가 넘어오고 옵션이 선택 가능할 때만 태그 노출. 이벤트 종료 시 관련 로직 제거
    const showLimitedTag = !!originalPrice && isOptionSelectable;
    return /*#__PURE__*/ _jsx(PurchaseFormControlLabel, {
        status: status,
        value: isOptionAvailable !== "all" ? optionType === isOptionAvailable ? optionType : null : optionType,
        control: /*#__PURE__*/ _jsx(Radio, {
            sx: {
                display: "none"
            }
        }),
        sx: (theme)=>({
                padding: theme.spacing(1),
                position: "relative",
                overflow: "visible"
            }),
        label: /*#__PURE__*/ _jsxs(Typography, {
            as: "span",
            variant: "subtitle1",
            className: cn("relative flex w-full flex-col items-center", labelColorClassMap[status]),
            children: [
                showLimitedTag && /*#__PURE__*/ _jsx(Tag, {
                    color: "red",
                    size: "sm",
                    accent: true,
                    bold: true,
                    className: "absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap",
                    children: "기간 한정 할인"
                }),
                label,
                /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    variant: "caption",
                    className: cn(status === "disabled" ? "text-disabled" : "text-secondary"),
                    children: isOptionSelectable ? price.toLocaleString() + "원" : "미제공자료"
                })
            ]
        })
    });
};
export default PurchaseOption;
